import { lazy, Suspense, useEffect, useState, type CSSProperties, type HTMLAttributes, type ReactNode } from "react";
import { SKY_PALETTES } from "@/data/sky";
import { useSkyTheme } from "@/theme/sky-theme-core";

const SkyGradientCanvas = lazy(async () => ({
  default: (await import("./SkyGradientCanvas")).SkyGradientCanvas,
}));

type SkyBackdropElement = "div" | "header" | "section";

interface SkyBackdropProps extends Omit<HTMLAttributes<HTMLElement>, "children"> {
  as?: SkyBackdropElement;
  children: ReactNode;
  renderMode?: "css" | "landing";
}

export function SkyBackdrop({ as: Element = "div", children, className = "", renderMode = "css", style, ...props }: SkyBackdropProps) {
  const { tone } = useSkyTheme();
  const [reducedMotion, setReducedMotion] = useState(false);
  const palette = SKY_PALETTES[tone];
  const supportsWebGl = typeof window !== "undefined" && "WebGLRenderingContext" in window;

  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setReducedMotion(query.matches);
    sync();
    query.addEventListener("change", sync);
    return () => query.removeEventListener("change", sync);
  }, []);
  const skyStyle = {
    "--theme-sky-top": palette.top,
    "--theme-sky-horizon": palette.horizon,
    "--theme-sky-bottom": palette.bottom,
    ...style,
  } as CSSProperties;

  return (
    <Element
      {...props}
      className={`studio-sky-backdrop ${className}`.trim()}
      data-sky-surface="true"
      data-sky-tone={tone}
      data-sky-render-mode={renderMode}
      style={skyStyle}
    >
      {renderMode === "landing" && supportsWebGl && <Suspense fallback={null}><SkyGradientCanvas tone={tone} reducedMotion={reducedMotion} /></Suspense>}
      {children}
    </Element>
  );
}
